<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading'],
  data() {
    return {
      loading: true,
      announcements: [],
      unreadCount: 0,
      pagination: {
        current_page: 1,
        per_page: 20,
        last_page: 1,
        total: 0,
      },
    };
  },
  mounted() {
    this.fetchAnnouncements();
    this.fetchUnreadCount();
  },
  methods: {
    async fetchAnnouncements(page = 1) {
      this.loading = true;
      try {
        const response = await axios.get('/hrms/announcements/my', { params: { page } });
        this.announcements = response.data.data.data;
        this.pagination = {
          current_page: response.data.data.current_page,
          per_page: response.data.data.per_page,
          last_page: response.data.data.last_page,
          total: response.data.data.total,
        };
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.loading = false;
      }
    },

    async fetchUnreadCount() {
      try {
        const res = await axios.get('/hrms/announcements/unread-count');
        this.unreadCount = res.data.data.unread_count || 0;
      } catch (error) {
        console.log(error.message);
      }
    },

    typeClass(type) {
      const map = {
        general: 'ma-tag ma-general',
        urgent: 'ma-tag ma-urgent',
        event: 'ma-tag ma-event',
        policy: 'ma-tag ma-policy',
      };
      return map[type] || 'ma-tag ma-general';
    },

    goToPage(page) {
      if (page < 1 || page > this.pagination.last_page) return;
      this.fetchAnnouncements(page);
    },

    // Opens detail modal; the GET-by-id call marks it as read server-side
    async openAnnouncement(item) {
      this.$refs.viewModal.open(item.id);
      this.$openModal('hrm_announcement_view_modal');

      // Optimistically flip local read state + refresh unread badge
      if (!item.is_read) {
        item.is_read = true;
        this.fetchUnreadCount();
      }
    },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Header -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <div class="d-flex align-items-center gap-2">
          <h4 class="page-title mb-0">My Announcements</h4>
          <span v-if="unreadCount > 0" class="ma-unread-pill">{{ unreadCount }} unread</span>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="ma-state">
        <div class="spinner-border text-primary mb-2"></div>
        <div>Loading announcements...</div>
      </div>

      <!-- Empty -->
      <div v-else-if="!announcements.length" class="ma-state">
        <i class="ti ti-speakerphone d-block mb-2" style="font-size:38px; opacity:.35;"></i>
        No announcements yet.
      </div>

      <!-- List -->
      <div v-else class="d-flex flex-column gap-2">
        <div
          v-for="item in announcements"
          :key="item.id"
          class="ma-row"
          :class="{ 'ma-row-unread': !item.is_read }"
          @click="openAnnouncement(item)"
        >
          <div class="ma-row-dot-wrap">
            <span v-if="!item.is_read" class="ma-dot"></span>
          </div>

          <div class="ma-row-body">
            <div class="d-flex align-items-center gap-2 flex-wrap mb-1">
              <span :class="typeClass(item.type)">{{ item.type }}</span>
              <span class="ma-row-title" :class="{ 'fw-bold': !item.is_read }">{{ item.title }}</span>
            </div>
            <p class="ma-row-content">{{ item.content }}</p>
          </div>

          <div class="ma-row-date">
            {{ $formatDate(item.published_at) }}
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="!loading && announcements.length && pagination.last_page > 1" class="d-flex justify-content-center align-items-center gap-2 mt-4">
        <button class="btn btn-sm btn-light" :disabled="pagination.current_page === 1" @click="goToPage(pagination.current_page - 1)">
          <i class="ti ti-chevron-left"></i>
        </button>
        <span style="font-size:12.5px; color:#64748b;">
          Page {{ pagination.current_page }} of {{ pagination.last_page }}
        </span>
        <button class="btn btn-sm btn-light" :disabled="pagination.current_page === pagination.last_page" @click="goToPage(pagination.current_page + 1)">
          <i class="ti ti-chevron-right"></i>
        </button>
      </div>

    </div>
    <app-footer></app-footer>
  </div>

  <!-- View / Detail Modal -->
  <hrm-announcement-view-modal ref="viewModal"></hrm-announcement-view-modal>
</template>

<style scoped>
.ma-unread-pill {
  font-size: 11px; font-weight: 700; color: #fff;
  background: #dc2626; padding: 3px 10px; border-radius: 20px;
}

.ma-state {
  text-align: center; padding: 60px 20px;
  color: #94a3b8; font-size: 13.5px;
  background: #fff; border-radius: 14px;
}

.ma-row {
  display: flex; align-items: flex-start; gap: 10px;
  background: #fff; border: 1px solid #eef2f7; border-radius: 12px;
  padding: 14px 16px; cursor: pointer;
  transition: all .15s ease;
}
.ma-row:hover { border-color: #c7d2fe; box-shadow: 0 4px 14px rgba(99,102,241,.08); }
.ma-row-unread { background: #f8fafc; border-color: #e0e7ff; }

.ma-row-dot-wrap { width: 10px; padding-top: 6px; flex-shrink: 0; }
.ma-dot { display: block; width: 8px; height: 8px; border-radius: 50%; background: #6366f1; }

.ma-row-body { flex: 1; min-width: 0; }
.ma-row-title { font-size: 13.5px; color: #1e293b; }
.ma-row-content {
  font-size: 12px; color: #64748b; margin: 0;
  display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical;
  overflow: hidden;
}

.ma-row-date { font-size: 11px; color: #94a3b8; white-space: nowrap; padding-top: 2px; }

.ma-tag {
  display: inline-block; font-size: 10px; font-weight: 600;
  padding: 2px 9px; border-radius: 20px; text-transform: capitalize;
}
.ma-general { background: #eef2ff; color: #4f46e5; }
.ma-urgent { background: #fee2e2; color: #dc2626; }
.ma-event { background: #fef3c7; color: #b45309; }
.ma-policy { background: #e0f2fe; color: #0369a1; }
</style>